/**
 * Styles residuais do Cockpit do Lead (ADR-0041 / SPIN Selling).
 *
 * O grosso usa classes canonicas (.eh / .kpi-strip-v2 / .health-ring /
 * .crm-tl / .btn / .badge) + componentes compartilhados (EntityHeader,
 * KpiStrip, Block, SlideOverPanel, AsyncButton). Aqui ficam apenas os
 * residuos sem equivalente canonico: o grid de 2 colunas do cockpit, os
 * cartoes SPIN, listas de notas/tarefas/anexos/compromissos e o input de
 * tags (chips). Tudo via theme tokens (dark mode) — nada de cor hard-coded.
 */
import styled from "styled-components";

import { getToneTokens } from "../../../styles/shared";

// ---------- Layout 2 colunas (acoes a esquerda + conteudo a direita) ----------
//
// Desktop: esquerda mais estreita (acoes/dados/responsavel, sticky) +
// direita mais larga (secoes de conteudo). Mobile: empilha em 1 coluna com
// o CONTEUDO PRINCIPAL aparecendo primeiro (via order), e a barra de acoes
// embaixo. No DOM a LeftRail vem primeiro para o desktop posicionar pela
// ordem-fonte (col 1 = esquerda, col 2 = direita).

export const CockpitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: start;

  @media (min-width: 1100px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.9fr);
  }
`;

// Coluna esquerda (acoes rapidas, dados, responsavel). Sticky no desktop.
export const LeftRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  order: 2; /* mobile: depois do conteudo */

  @media (min-width: 1100px) {
    order: 0;
    position: sticky;
    top: 96px;
  }
`;

// Coluna direita (secoes de conteudo). No mobile aparece primeiro.
export const MainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  order: 1; /* mobile: antes da barra de acoes */

  @media (min-width: 1100px) {
    order: 0;
  }
`;

// ---------- SPIN — health summary (anel + legenda) ----------

export const SpinHealthWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SpinHealthLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textSecondary};
`;

// ---------- SPIN — bloco de estagio ----------

export const SpinEstagioGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const SpinCard = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  border-left: 3px solid
    ${({ theme, $preenchido }) =>
      $preenchido ? getToneTokens(theme, "success").text : theme.border};
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${({ theme }) => theme.surface};
`;

export const SpinCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const SpinCardTitle = styled.h4`
  margin: 0;
  font-size: 0.92rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
`;

export const SpinRoteiro = styled.p`
  margin: 0;
  font-size: 0.76rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.textSecondary};
  font-style: italic;
  padding: 8px 10px;
  background: ${({ theme }) => theme.surfaceAlt};
  border-radius: 8px;
`;

// ---------- Notas (lista de itens editaveis) ----------

export const NotaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
  border-bottom: 1px dashed ${({ theme }) => theme.border};

  &:last-child {
    border-bottom: 0;
  }
`;

export const NotaTexto = styled.div`
  font-size: 0.86rem;
  color: ${({ theme }) => theme.textPrimary};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const NotaMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.textTertiary};
`;

export const ItemActions = styled.div`
  display: flex;
  gap: 4px;
  margin-left: auto;
`;

// Botao de acao discreto (icone) usado em listas (editar/excluir/fixar).
export const MiniButton = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: ${({ theme, $tone }) =>
    $tone === "danger" ? theme.dangerText : theme.textSecondary};
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.74rem;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme, $tone }) =>
      $tone === "danger" ? theme.dangerText : theme.textPrimary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ---------- Tarefas + subtarefas ----------

export const TarefaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.border};

  &:last-child {
    border-bottom: 0;
  }
`;

export const CheckRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const CheckLabel = styled.span`
  flex: 1;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.textPrimary};
  text-decoration: ${({ $done }) => ($done ? "line-through" : "none")};
  opacity: ${({ $done }) => ($done ? 0.6 : 1)};
  overflow-wrap: anywhere;
`;

export const SubLista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 26px;
  padding-left: 10px;
  border-left: 2px solid ${({ theme }) => theme.border};
`;

export const SubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SubLabel = styled.span`
  flex: 1;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.textSecondary};
  text-decoration: ${({ $done }) => ($done ? "line-through" : "none")};
  opacity: ${({ $done }) => ($done ? 0.6 : 1)};
  overflow-wrap: anywhere;
`;

// Linha de adicionar (input + botao) reutilizada por notas/tarefas/subtarefas.
export const AddRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

// ---------- Orcamentos / Compromissos (linha simples) ----------

export const LinhaItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px dashed ${({ theme }) => theme.border};
  font-size: 0.86rem;

  &:last-child {
    border-bottom: 0;
  }
`;

export const LinhaInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const LinhaTitulo = styled.span`
  font-size: 0.88rem;
  color: ${({ theme }) => theme.textPrimary};
  overflow-wrap: anywhere;
`;

export const LinhaSub = styled.span`
  font-size: 0.74rem;
  color: ${({ theme }) => theme.textTertiary};
`;

// ---------- Tags (chips editaveis) ----------

export const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

export const TagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.74rem;
  background: ${({ theme }) => getToneTokens(theme, "info").background};
  color: ${({ theme }) => getToneTokens(theme, "info").text};
  border: 1px solid ${({ theme }) => getToneTokens(theme, "info").border};

  > button {
    appearance: none;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    font-size: 0.9rem;
  }
`;

// ---------- Edicao inline (sidebar Dados) ----------

export const InlineField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const InlineLabel = styled.span`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.textSecondary};
`;
